import { ProductVariant } from '../types/product'
import { 
  CubeIcon,
  CurrencyEuroIcon,
  TagIcon,
  PhotoIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

interface VariantBasicInfoTabProps {
  variant: ProductVariant
}

const VariantBasicInfoTab = ({ variant }: VariantBasicInfoTabProps) => {
  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <CubeIcon className="h-4 w-4 mr-2" />
                Variant Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.name}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <CurrencyEuroIcon className="h-4 w-4 mr-2" />
                Price
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{formatPrice(variant.price_cents)}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <TagIcon className="h-4 w-4 mr-2" />
                SKU
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.sku || 'Not set'}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Size</dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.size || 'Not set'}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  variant.active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {variant.active ? 'Active' : 'Inactive'}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Product Information */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Product Information</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Parent Product</dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.product?.name || 'Unknown'}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Product ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.product_id || 'Not set'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Additional Details */}
      {(variant.description || variant.image_url) && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Additional Details</h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6">
              {variant.description && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Description
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{variant.description}</dd>
                </div>
              )}
              
              {variant.image_url && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <PhotoIcon className="h-4 w-4 mr-2" />
                    Image
                  </dt>
                  <dd className="mt-1">
                    <img 
                      src={variant.image_url} 
                      alt={variant.name}
                      className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                    />
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* System Information */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">System Information</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Variant ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{variant.id}</dd>
            </div>
            
            {variant.created_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(variant.created_at).toLocaleDateString()}
                </dd>
              </div>
            )}
            
            {variant.updated_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(variant.updated_at).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  )
}

export default VariantBasicInfoTab

